/**
 * DateRangePicker — Calendar-based date range selector (Cost Explorer style).
 * Two side-by-side month calendars. Click to select start, click again to select end.
 */

import { useState, useCallback } from 'react';
import { LdsBox, LdsButton, LdsPaper } from '@lds/react';
import styles from './DateRangePicker.module.scss';

export interface DateRangePickerProps {
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
  onApply: (from: string, to: string) => void;
  onClose: () => void;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function parseDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isBetween(d: Date, start: Date, end: Date): boolean {
  return d > start && d < end;
}

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export function DateRangePicker({ from, to, onApply, onClose }: DateRangePickerProps) {
  const initialFrom = parseDate(from);
  const initialTo = parseDate(to);

  // The left calendar shows this month, right shows next month
  const [viewMonth, setViewMonth] = useState(() => {
    const d = parseDate(from);
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const [rangeStart, setRangeStart] = useState<Date | null>(initialFrom);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(initialTo);
  const [selecting, setSelecting] = useState<'start' | 'end'>('start');

  const handleDayClick = useCallback((day: Date) => {
    if (selecting === 'start') {
      setRangeStart(day);
      setRangeEnd(null);
      setSelecting('end');
    } else {
      if (rangeStart && day >= rangeStart) {
        setRangeEnd(day);
      } else {
        // Clicked before start — reset
        setRangeStart(day);
        setRangeEnd(null);
      }
      setSelecting('start');
    }
  }, [selecting, rangeStart]);

  const handleApply = () => {
    if (rangeStart && rangeEnd) {
      onApply(formatDate(rangeStart), formatDate(rangeEnd));
    }
  };

  const prevMonth = () => {
    setViewMonth((v) => {
      if (v.month === 0) return { year: v.year - 1, month: 11 };
      return { year: v.year, month: v.month - 1 };
    });
  };

  const nextMonth = () => {
    setViewMonth((v) => {
      if (v.month === 11) return { year: v.year + 1, month: 0 };
      return { year: v.year, month: v.month + 1 };
    });
  };

  const rightMonth = viewMonth.month === 11
    ? { year: viewMonth.year + 1, month: 0 }
    : { year: viewMonth.year, month: viewMonth.month + 1 };

  return (
    <LdsPaper elevated className={styles.picker}>
      <LdsBox className={styles.header}>
        <LdsButton variant="basic" onClick={prevMonth} aria-label="Mês anterior">‹</LdsButton>
        <span className={styles.monthTitle}>
          {MONTH_NAMES[viewMonth.month]} {viewMonth.year}
        </span>
        <span className={styles.monthTitle}>
          {MONTH_NAMES[rightMonth.month]} {rightMonth.year}
        </span>
        <LdsButton variant="basic" onClick={nextMonth} aria-label="Próximo mês">›</LdsButton>
      </LdsBox>

      <LdsBox className={styles.calendars}>
        <MonthGrid
          year={viewMonth.year}
          month={viewMonth.month}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          onDayClick={handleDayClick}
        />
        <MonthGrid
          year={rightMonth.year}
          month={rightMonth.month}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          onDayClick={handleDayClick}
        />
      </LdsBox>

      <LdsBox className={styles.footer}>
        <span className={styles.rangeDisplay}>
          {rangeStart ? formatDate(rangeStart) : '—'} → {rangeEnd ? formatDate(rangeEnd) : '—'}
        </span>
        <LdsBox className={styles.footerActions}>
          <LdsButton variant="basic" onClick={onClose}>Cancelar</LdsButton>
          <LdsButton variant="contained" onClick={handleApply} disabled={!rangeStart || !rangeEnd}>
            Aplicar
          </LdsButton>
        </LdsBox>
      </LdsBox>
    </LdsPaper>
  );
}

interface MonthGridProps {
  year: number;
  month: number;
  rangeStart: Date | null;
  rangeEnd: Date | null;
  onDayClick: (day: Date) => void;
}

function MonthGrid({ year, month, rangeStart, rangeEnd, onDayClick }: MonthGridProps) {
  const totalDays = daysInMonth(year, month);
  const firstDow = new Date(year, month, 1).getDay(); // 0=Sun

  const cells: (Date | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));

  return (
    <div className={styles.monthGrid}>
      <div className={styles.weekRow}>
        {WEEKDAYS.map((w) => <span key={w} className={styles.weekday}>{w}</span>)}
      </div>
      <div className={styles.daysGrid}>
        {cells.map((day, idx) => {
          if (!day) return <span key={`e-${idx}`} className={styles.emptyCell} />;

          let cellClass = styles.dayCell;
          if (rangeStart && isSameDay(day, rangeStart)) cellClass += ` ${styles.rangeEdge}`;
          else if (rangeEnd && isSameDay(day, rangeEnd)) cellClass += ` ${styles.rangeEdge}`;
          else if (rangeStart && rangeEnd && isBetween(day, rangeStart, rangeEnd)) cellClass += ` ${styles.inRange}`;

          return (
            <button
              key={day.getDate()}
              className={cellClass}
              onClick={() => onDayClick(day)}
              type="button"
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
